import { useLayoutEffect, useRef } from 'react';
import { cn } from '@/utils/cn';
import { MotdText } from '../server/mc/MotdText';

const COLORS = [
  { code: 'f', hex: '#FFFFFF', name: 'Weiß' },
  { code: '7', hex: '#AAAAAA', name: 'Grau' },
  { code: '8', hex: '#555555', name: 'Dunkelgrau' },
  { code: '0', hex: '#000000', name: 'Schwarz' },
  { code: 'e', hex: '#FFFF55', name: 'Gelb' },
  { code: '6', hex: '#FFAA00', name: 'Gold' },
  { code: 'c', hex: '#FF5555', name: 'Rot' },
  { code: '4', hex: '#AA0000', name: 'Dunkelrot' },
  { code: 'd', hex: '#FF55FF', name: 'Pink' },
  { code: '5', hex: '#AA00AA', name: 'Lila' },
  { code: '9', hex: '#5555FF', name: 'Blau' },
  { code: '1', hex: '#0000AA', name: 'Dunkelblau' },
  { code: 'b', hex: '#55FFFF', name: 'Aqua' },
  { code: '3', hex: '#00AAAA', name: 'Türkis' },
  { code: 'a', hex: '#55FF55', name: 'Hellgrün' },
  { code: '2', hex: '#00AA00', name: 'Dunkelgrün' },
];

const FORMATS = [
  { code: 'l', label: 'F', name: 'Fett', cls: 'font-bold' },
  { code: 'o', label: 'K', name: 'Kursiv', cls: 'italic' },
  { code: 'n', label: 'U', name: 'Unterstrichen', cls: 'underline' },
  { code: 'r', label: '↺', name: 'Formatierung zurücksetzen', cls: '' },
];

interface Props {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  label?: string;
  onEnter?: () => void;
  /** §-Codes/Text, der in der Vorschau vor der Nachricht steht (z. B. „§6§l[Server] §r§e"). */
  previewPrefix?: string;
}

// Knöpfe dürfen dem Eingabefeld den Fokus nicht wegnehmen – sonst ginge die Markierung verloren.
const keepFocus = (e: React.MouseEvent) => e.preventDefault();

/**
 * Eingabe für Minecraft-Chattext mit Farbpunkten (Name beim Drüberfahren),
 * Fett/Kursiv/Unterstrichen und Live-Vorschau – statt kryptischer „0–9 a–f"-Knöpfe.
 * Ist Text markiert, wird nur der markierte Teil eingefärbt.
 */
export const McTextComposer = ({ value, onChange, placeholder, label, onEnter, previewPrefix = '' }: Props) => {
  const ref = useRef<HTMLInputElement>(null);
  const caret = useRef<number | null>(null);

  // Cursor direkt nach dem Übernehmen des neuen Werts setzen (vorher per requestAnimationFrame –
  // wer schnell weitertippte, bekam vertauschte Buchstaben: „dgol" statt „gold").
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && caret.current !== null) {
      el.focus();
      el.setSelectionRange(caret.current, caret.current);
      caret.current = null;
    }
  }, [value]);

  const insert = (code: string) => {
    const token = `&${code}`;
    const el = ref.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? start;
    const selected = value.slice(start, end);
    const next = selected
      ? value.slice(0, start) + token + selected + '&r' + value.slice(end)
      : value.slice(0, start) + token + value.slice(end);
    caret.current = start + token.length + selected.length + (selected ? 2 : 0);
    onChange(next);
  };

  const preview = previewPrefix + value.replace(/&([0-9a-fk-or])/gi, '§$1');

  return (
    <div className="space-y-2">
      {label && <label className="block text-[11px] uppercase tracking-wider text-fg-muted font-medium ml-0.5">{label}</label>}
      <input
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && onEnter) { e.preventDefault(); onEnter(); } }}
        placeholder={placeholder}
        className="block w-full rounded-lg bg-black/25 text-fg placeholder-fg-muted/60 border border-line focus:outline-none focus:border-ember/70 focus:ring-2 focus:ring-ember/20 hover:border-line-strong transition-all px-3.5 py-2.5 text-sm"
      />
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] text-fg-muted mr-0.5">Farbe</span>
        {COLORS.map((c) => (
          <button
            type="button"
            key={c.code}
            title={`${c.name} (&${c.code})`}
            aria-label={c.name}
            onMouseDown={keepFocus}
            onClick={() => insert(c.code)}
            className="w-5 h-5 rounded-full border border-white/20 hover:scale-125 transition-transform"
            style={{ backgroundColor: c.hex }}
          />
        ))}
        <span className="w-px h-4 bg-line mx-1" />
        {FORMATS.map((f) => (
          <button
            type="button"
            key={f.code}
            title={`${f.name} (&${f.code})`}
            aria-label={f.name}
            onMouseDown={keepFocus}
            onClick={() => insert(f.code)}
            className={cn('w-6 h-6 rounded-md border border-line text-xs text-fg-muted hover:text-fg hover:border-line-strong transition-colors', f.cls)}
          >
            {f.label}
          </button>
        ))}
      </div>
      {value.trim() ? (
        <div className="flex items-baseline gap-2 rounded-lg bg-black/40 border border-line px-3 py-2 min-w-0">
          <span className="text-[10px] uppercase tracking-wider text-fg-muted shrink-0">Vorschau</span>
          <MotdText text={preview} className="font-mono text-[13px] text-white truncate" />
        </div>
      ) : (
        <p className="text-[11px] text-fg-muted">Tipp: Text markieren und eine Farbe wählen – dann wird nur der markierte Teil eingefärbt.</p>
      )}
    </div>
  );
};
